/**
 * Desktop heatmap — restored CSS-table renderer (v1 style).
 *
 * Why a table instead of canvas: the canvas renderer (kept in
 * HeatmapCanvas.tsx as an option) shrinks cells to fit the viewport.
 * That makes the values illegible. The original v1 used a real
 * scrollable <table> with every cell ~140px wide × 22px tall, every
 * non-zero cell labelled, and strong row treatments for the spot /
 * king / gatekeeper / flip rows. This is that, with v2's data
 * (Tradier provider, prev_close, regime, etc.) wired in.
 */

import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { HeatmapData, HeatmapCell } from '../../hooks/useHeatmapData';
import { ViewMode } from '../../lib/types';
import { createCellColorScale, getCellTextColor, formatExposureValue, WALL_PEAK_BAND } from '../../lib/colors';
import { NODE_INDICATORS } from '../../lib/constants';
import { StrikeWindow } from '../../hooks/useStrikeWindow';
import { useThemeKey } from '../../hooks/useThemeKey';
import { formatStrike } from '../../lib/format';

// How long to show "Connecting…" before flipping to "no data on the wire."
// First chain pull for a cold ticker runs ~1-3 s with the new Tradier
// concurrency settings (semaphore=8 in providers/tradier.py), but slow
// Tradier days can push it to 30+ s. 90 s gives plenty of margin so the
// page doesn't look broken while it's just waiting on the pipeline.
// Past that the stream is connected but truly empty — outside market
// hours, a delisted symbol, or a Tradier outage.
const EMPTY_TIMEOUT_MS = 90_000;

interface HeatmapTableProps {
  data: HeatmapData | null;
  viewMode: ViewMode;
  flipPoint: number | null;
  ticker: string;
  window: StrikeWindow;
}

function applyWindow(data: HeatmapData, win: StrikeWindow): HeatmapData {
  if (win === 0 || !data.strikes.length) return data;
  let spotIdx = data.strikes.findIndex(s => s >= data.spotPrice);
  if (spotIdx < 0) spotIdx = data.strikes.length - 1;
  const lo = Math.max(0, spotIdx - win);
  const hi = Math.min(data.strikes.length - 1, spotIdx + win);
  if (lo === 0 && hi === data.strikes.length - 1) return data;
  const strikes = data.strikes.slice(lo, hi + 1);
  const cells = data.cells.slice(lo, hi + 1);
  // Preserve the original maxAbsValue from the full dataset so the color
  // scale stays stable when toggling between ±15 and ALL. Re-compute only
  // as a fallback when the frame doesn't carry a pre-computed value.
  let maxAbsValue = data.maxAbsValue > 0 ? data.maxAbsValue : 0;
  if (maxAbsValue === 0) {
    for (const row of cells) for (const c of row) {
      const a = Math.abs(c.value);
      if (a > maxAbsValue) maxAbsValue = a;
    }
  }
  return { ...data, strikes, cells, maxAbsValue };
}

function fmtExp(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const months = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
  return `${months[parseInt(m[2], 10) - 1]}-${m[3]}`;
}

// ── Memoized row ──────────────────────────────────────────────────────────
// Re-renders only when its own strike's data changes, not on every WS frame.
// Each row is responsible for rendering the strike label + all of its cells.
// The colorFn reference is stable within a computed block (same maxAbsValue),
// so shallowEqual on the props below is accurate.

interface RowProps {
  strike: number;
  row: HeatmapCell[];
  dates: string[];
  /** Per-column max |value| + per-column color fn, index-aligned to `dates`. */
  colMaxAbs: number[];
  colColorFns: ((v: number) => string)[];
  /** Per-column peak |gamma| per sign — most-negative value (PUT WALL) and
      most-positive value (CALL WALL), index-aligned to `dates`. A cell is a
      wall if it's same-sign AND within WALL_PEAK_BAND of its column's peak. */
  colMinNegVals: number[];
  colMaxPosVals: number[];
  /** Resolved --gv-pos / --gv-neg hex for the peak walls (so textOnCell
      can pick a contrasting label). Re-read on theme change via themeKey. */
  posHex: string;
  negHex: string;
  /** Active theme — forces a re-render (and re-read of cell colors) on a
      theme swap, since colMaxAbs alone wouldn't change. */
  themeKey: string;
  isSpot: boolean;
  isKing: boolean;
  /** True when the King node's value is negative (short-gamma King) —
      switches the row accent from amber to the purple day-low family. */
  isKingNeg: boolean;
  isCounterKing: boolean;
  isGk: boolean;
  isFlip: boolean;
  spotRowRef?: React.Ref<HTMLTableRowElement>;
  viewMode: ViewMode;
}

const HeatmapRow = memo<RowProps>(function HeatmapRow({
  strike, row, dates, colMaxAbs, colColorFns, colMinNegVals,
  colMaxPosVals, posHex, negHex, themeKey,
  isSpot, isKing, isKingNeg, isCounterKing, isGk, isFlip,
  spotRowRef, viewMode,
}) {
  void themeKey; // referenced only to force re-render on theme swap (see memo eq)
  const rowClass = ['hm-row',
    isSpot ? 'is-spot' : '',
    isKing ? 'is-king' : '',
    isKing && isKingNeg ? 'is-king-neg' : '',
    isCounterKing ? 'is-counter-king' : '',
    isGk   ? 'is-gk'   : '',
    isFlip ? 'is-flip' : '',
  ].filter(Boolean).join(' ');

  return (
    <tr key={strike} className={rowClass} ref={spotRowRef}>
      <td className="hm-strike-cell">
        {formatStrike(strike)}
        {isFlip && <span className="hm-flip-tag">FLIP</span>}
        {isKing && <span className="hm-king-tag" aria-hidden="true">★</span>}
        {isCounterKing && <span className="hm-counter-king-tag" aria-label="Counter-King (largest opposite-sign GEX node)">✦</span>}
        {isGk && !isKing && !isCounterKing && <span className="hm-gk-tag" aria-hidden="true">◆</span>}
      </td>
      {row.map((cell, colIdx) => {
        // Per expiration, accent the column's largest long (CALL WALL →
        // --gv-pos) and largest short (PUT WALL → --gv-heat-neg), PLUS any
        // same-sign cell within WALL_PEAK_BAND of that peak |gamma| (so a
        // near-tie reads as a wall too). Everything else = muted base.
        const isCallWall = cell.value > 0 && cell.value >= colMaxPosVals[colIdx] * (1 - WALL_PEAK_BAND);
        const isPutWall  = cell.value < 0 && cell.value <= colMinNegVals[colIdx] * (1 - WALL_PEAK_BAND);
        const bg = isCallWall ? posHex
                 : isPutWall  ? negHex
                 : colColorFns[colIdx](cell.value);
        const fg = isCallWall || isPutWall
                 ? getCellTextColor(cell.value, 0, bg)
                 : getCellTextColor(cell.value, colMaxAbs[colIdx] ?? 0, bg);
        const display = Math.abs(cell.value) > 0.5 ? formatExposureValue(cell.value) : '';
        // Node glyphs (★/✦/◆) render ONLY in the front (closest)
        // expiration's cell — King is front-scoped now, and repeating the
        // glyph across every column of the row was pure noise. The strike
        // rail keeps the row-level tag for wayfinding.
        const indicator = colIdx === 0 && cell.node
          ? (NODE_INDICATORS[cell.node.node_type] || '')
          : '';
        return (
          <td
            key={colIdx}
            className={`hm-cell${cell.value > 0 ? ' pos' : cell.value < 0 ? ' neg' : ''}${isCallWall ? ' is-call-wall' : ''}${isPutWall ? ' is-put-wall' : ''}`}
            style={{ backgroundColor: bg, color: fg }}
            title={`Strike $${formatStrike(strike)} · ${dates[colIdx]} · ${viewMode.toUpperCase()} ${cell.value.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}${isCallWall ? ' · CALL WALL' : isPutWall ? ' · PUT WALL (this expiry)' : ''}`}
          >
            <span className="hm-cell-inner">
              <span className="hm-cell-val">{display}</span>
              {indicator && <span className="hm-cell-ind">{indicator}</span>}
            </span>
          </td>
        );
      })}
    </tr>
  );
}, (prev, next) => {
  // Custom equality: skip re-render if the strike's cell values, colors,
  // and row-class flags haven't changed. This fires on every WS frame so
  // the comparison must be cheap: check scalar flags first (fast-exit),
  // then the maxAbsValue (determines colors), then the cell value array.
  // themeKey first: a theme swap changes every cell color (the colorFns
  // re-read CSS vars) without changing colMaxAbs, so it MUST invalidate.
  if (prev.themeKey !== next.themeKey) return false;
  if (prev.isSpot !== next.isSpot) return false;
  if (prev.isKing !== next.isKing) return false;
  if (prev.isKingNeg !== next.isKingNeg) return false;
  if (prev.isCounterKing !== next.isCounterKing) return false;
  if (prev.isGk !== next.isGk) return false;
  if (prev.isFlip !== next.isFlip) return false;
  // Re-render when any column ceiling changes (drives per-column colors).
  if (prev.colMaxAbs.length !== next.colMaxAbs.length) return false;
  for (let i = 0; i < prev.colMaxAbs.length; i++) {
    if (prev.colMaxAbs[i] !== next.colMaxAbs[i]) return false;
  }
  // Per-column peak values drive wall membership; a change in any column's
  // peak must re-render every row (a cell can cross the band threshold).
  if (prev.colMinNegVals.length !== next.colMinNegVals.length) return false;
  for (let i = 0; i < prev.colMinNegVals.length; i++) {
    if (prev.colMinNegVals[i] !== next.colMinNegVals[i]) return false;
  }
  if (prev.colMaxPosVals.length !== next.colMaxPosVals.length) return false;
  for (let i = 0; i < prev.colMaxPosVals.length; i++) {
    if (prev.colMaxPosVals[i] !== next.colMaxPosVals[i]) return false;
  }
  if (prev.viewMode !== next.viewMode) return false;
  if (prev.row.length !== next.row.length) return false;
  for (let i = 0; i < prev.row.length; i++) {
    if (prev.row[i].value !== next.row[i].value) return false;
    if (prev.row[i].node?.node_type !== next.row[i].node?.node_type) return false;
  }
  return true;
});

const HeatmapTable: React.FC<HeatmapTableProps> = ({ data, viewMode, flipPoint, ticker, window: win }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const spotRowRef = useRef<HTMLTableRowElement>(null);
  const hasScrolled = useRef(false);
  const themeKey = useThemeKey();

  // Track how long we've been in the empty state. After EMPTY_TIMEOUT_MS,
  // flip from "Connecting…" to the "no data on the wire" message so the
  // user knows the stream is up but the symbol returned nothing.
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    if (data) {
      setTimedOut(false);
      return;
    }
    // If we get here, data is null — start the 30s timer.
    const id = setTimeout(() => setTimedOut(true), EMPTY_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [data, ticker]);

  const filtered = useMemo(() => (data ? applyWindow(data, win) : null), [data, win]);

  // Auto-scroll to spot row on first load and after every ticker switch.
  useEffect(() => { hasScrolled.current = false; }, [ticker]);
  useEffect(() => {
    if (hasScrolled.current || !spotRowRef.current || !containerRef.current) return;
    const c = containerRef.current;
    const r = spotRowRef.current;
    const cr = c.getBoundingClientRect();
    const rr = r.getBoundingClientRect();
    c.scrollTop += rr.top - cr.top - cr.height / 2 + r.offsetHeight / 2;
    hasScrolled.current = true;
  }, [filtered?.spotPrice]);

  const computed = useMemo(() => {
    if (!filtered) return null;
    const { strikes, cells, colMaxAbs, spotPrice, nodes, dates } = filtered;
    // Per-EXPIRATION color scales: each date column is normalized to its
    // own max so a single 0DTE near-ATM cell can't set one global ceiling
    // that washes every other column to a target-less faint wash. Cells
    // still print their dollar values, so absolute magnitude stays readable.
    const colColorFns = colMaxAbs.map(m => createCellColorScale(m));
    // Per-column peak |gamma| per sign — the most-negative (PUT WALL) and
    // most-positive (CALL WALL) value in each expiration. Cells within
    // WALL_PEAK_BAND of these get the accent (see HeatmapRow).
    const nCols = dates.length;
    const colMinNegVals: number[] = new Array(nCols).fill(0);
    const colMaxPosVals: number[] = new Array(nCols).fill(0);
    for (let r = 0; r < strikes.length; r++) {
      const rowCells = cells[r];
      for (let c = 0; c < nCols; c++) {
        const v = rowCells[c]?.value ?? 0;
        if (v < colMinNegVals[c]) colMinNegVals[c] = v;
        if (v > colMaxPosVals[c]) colMaxPosVals[c] = v;
      }
    }
    // Resolve the peak hues once (re-read on theme change via themeKey dep).
    const _css = getComputedStyle(document.documentElement);
    const posHex = _css.getPropertyValue('--gv-pos').trim() || '#facc15';
    const negHex = _css.getPropertyValue('--gv-heat-neg').trim() || '#760051';
    const reversedStrikes = [...strikes].reverse();
    const reversedCells = [...cells].reverse();

    const spotIdxAsc = strikes.findIndex(s => s >= spotPrice);
    const spotStrike = spotIdxAsc >= 0 ? strikes[spotIdxAsc] : null;

    const king = nodes.find(n => n.node_type === 'king');
    const counterKing = nodes.find(n => n.node_type === 'counter_king');
    const gkStrikes = new Set(nodes.filter(n => n.node_type === 'gatekeeper').map(n => n.strike));

    let flipStrike: number | null = null;
    if (flipPoint !== null) {
      let minDist = Infinity;
      for (const s of strikes) {
        const d = Math.abs(s - flipPoint);
        if (d < minDist) { minDist = d; flipStrike = s; }
      }
    }

    return {
      reversedStrikes, reversedCells, colColorFns, colMinNegVals, spotStrike,
      kingStrike: king?.strike ?? null,
      kingIsNeg: king ? !king.is_positive : false,
      counterKingStrike: counterKing?.strike ?? null,
      gkStrikes, flipStrike, dates, colMaxAbs,
      colMaxPosVals, posHex, negHex,
    };
  }, [filtered, flipPoint, themeKey]);

  if (!filtered || !computed) {
    return (
      <div className="hm-table-wrap">
        <div className="hm-table-empty">
          {timedOut ? (
            <>
              <p style={{ color: 'var(--text-2)' }}>No data on the wire — verify the symbol or check market hours.</p>
            </>
          ) : (
            <>
              <span className="gv-spinner" aria-hidden="true" />
              <p>Connecting to data stream…</p>
            </>
          )}
        </div>
      </div>
    );
  }

  const {
    reversedStrikes, reversedCells, colColorFns, colMinNegVals, spotStrike,
    kingStrike, kingIsNeg, counterKingStrike, gkStrikes, flipStrike, dates, colMaxAbs,
    colMaxPosVals, posHex, negHex,
  } = computed;

  return (
    <div className="hm-table-wrap" ref={containerRef} aria-label={`${viewMode.toUpperCase()} heatmap for ${ticker}`} role="region">
      <table className="hm-table">
        <thead>
          <tr>
            <th className="hm-strike-col">Strike</th>
            {dates.map(d => (
              <th key={d} className="hm-date-col">{fmtExp(d)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {reversedStrikes.map((strike, rowIdx) => {
            const row = reversedCells[rowIdx];
            const isSpot        = strike === spotStrike;
            const isKing        = strike === kingStrike;
            const isCounterKing = strike === counterKingStrike;
            const isGk          = gkStrikes.has(strike);
            const isFlip        = strike === flipStrike;

            return (
              <HeatmapRow
                key={strike}
                strike={strike}
                row={row}
                dates={dates}
                colMaxAbs={colMaxAbs}
                colColorFns={colColorFns}
                colMinNegVals={colMinNegVals}
                colMaxPosVals={colMaxPosVals}
                posHex={posHex}
                negHex={negHex}
                themeKey={themeKey}
                isSpot={isSpot}
                isKing={isKing}
                isKingNeg={kingIsNeg}
                isCounterKing={isCounterKing}
                isGk={isGk}
                isFlip={isFlip}
                spotRowRef={isSpot ? spotRowRef : undefined}
                viewMode={viewMode}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default HeatmapTable;
